import React from 'react';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

// 测试结果显示组件的Props
interface TestResultDisplayProps {
  title: string;
  subtitle?: string;
  testGroups: TestGroup[];
  className?: string;
}

const TestResultDisplay: React.FC<TestResultDisplayProps> = ({
  title,
  subtitle,
  testGroups,
  className = ''
}) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* 标题区域 */}
      <div className="text-center">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        {subtitle && <p className="text-gray-600">{subtitle}</p>}
      </div>

      {/* 测试组显示 */}
      <div className="space-y-4">
        {testGroups.map((group, groupIndex) => (
          <div
            key={groupIndex}
            className={`p-4 rounded-lg border ${
              group.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}
          >
            {/* 测试组标题 */}
            <h3 className="font-semibold text-gray-900 mb-2">
              {group.title}
              {group.score && ` (${group.score.current}/${group.score.total})`}
            </h3>

            {/* 测试项目列表 */}
            <div className="text-sm space-y-3">
              {group.items.map((item, itemIndex) => (
                <div key={itemIndex} className="space-y-3">
                  {/* 筛查问题显示：先问题，再答案 */}
                  {item.details && item.details.startsWith('问题：') ? (
                    <div className="space-y-2">
                      {/* 问题题干 - 突出显示 */}
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-900 font-medium leading-relaxed">
                          {item.details}
                        </p>
                      </div>
                      
                      {/* 用户答案 - 清晰显示 */}
                      <div className="flex items-center">
                        <span className="text-base font-medium mr-2">答案：{item.userAnswer}</span>
                        <span className="ml-2">
                          {item.passed ? (
                            <span className="text-green-600">✅</span>
                          ) : (
                            <span className="text-red-600">❌</span>
                          )}
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* 非筛查问题的正常显示 */
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center">
                          <span className="mr-2">{item.name}：</span>
                          <span className="font-medium">{item.userAnswer}</span>
                          <span className="ml-2">
                            {item.passed ? (
                              <span className="text-green-600">✅</span>
                            ) : (
                              <span className="text-red-600">❌</span>
                            )}
                          </span>
                        </span>
                        {item.correctAnswer && (
                          <span className="text-gray-500 text-xs">
                            (正确：{item.correctAnswer})
                          </span>
                        )}
                      </div>
                      
                      {/* 显示详细信息 */}
                      {item.details && (
                        <div className="ml-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 leading-relaxed">{item.details}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 逻辑错误提示 */}
            {group.hasLogicalError && (
              <p className="text-xs text-red-600 mt-2">
                ❌ 逻辑冲突：{group.errorReason}
              </p>
            )}

            {/* 特殊提示 */}
            {group.note && (
              <p className="text-xs text-gray-500 mt-2">{group.note}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TestResultDisplay;